import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FiUsers, FiUserCheck, FiAward, FiActivity, FiCalendar, FiRefreshCw } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import MahasiswaDashboard from './Mahasiswa/MahasiswaDashboard';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [periodePerbaikan, setPeriodePerbaikan] = useState([]);
  const [loadingPeriode, setLoadingPeriode] = useState(false);

  useEffect(() => {
    // Only fetch stats if not mahasiswa
    if (user?.role !== 'mahasiswa') {
      fetchStats();
      fetchPeriodePerbaikan();
    }
  }, [user]);

  // If user is mahasiswa, show mahasiswa dashboard
  if (user?.role === 'mahasiswa') {
    return <MahasiswaDashboard />;
  }

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/students/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodePerbaikan = async () => {
    setLoadingPeriode(true);
    try {
      const res = await axios.get('/api/neofeeder/periode-perbaikan');
      if (res.data.success) {
        setPeriodePerbaikan(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching periode perbaikan:', error);
    } finally {
      setLoadingPeriode(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return '-';
    }
  };

  // Custom tooltip for enrollment year chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Tahun {payload[0].payload._id}</p>
          <p style={{ margin: '5px 0 0 0', color: '#10b981' }}>
            {payload[0].value} mahasiswa
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="dashboard">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiActivity className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Dashboard</h1>
              <p className="header-subtitle">Selamat datang di Portal Admin Mahasiswa</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiUsers />
          </div>
          <div className="stat-details">
            <h3>{stats?.total || 0}</h3>
            <p>Total Mahasiswa</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiUserCheck />
          </div>
          <div className="stat-details">
            <h3>{stats?.active || 0}</h3>
            <p>Mahasiswa Aktif</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <FiAward />
          </div>
          <div className="stat-details">
            <h3>{stats?.alumni || 0}</h3>
            <p>Lulus</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="card">
          <h3>Mahasiswa per Program Studi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.byProgram || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="_id" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#4f46e5" name="Jumlah Mahasiswa" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3>Mahasiswa per Tahun Pendaftaran</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.byEnrollmentYear || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="_id" label={{ value: 'Tahun', position: 'insideBottom', offset: -5 }} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="count" fill="#10b981" name="Jumlah Mahasiswa" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top 3 Students by IPK */}
      <div className="card">
        <div className="card-header-with-filter">
          <h3>🏆 Top 3 Mahasiswa IPK Tertinggi</h3>
          {stats?.topStudentsByProgram && stats.topStudentsByProgram.length > 0 && (
            <select 
              className="program-filter-select"
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
            >
              <option value="">Semua Program Studi</option>
              {stats.topStudentsByProgram.map((prog) => (
                <option key={prog._id} value={prog._id}>
                  {prog._id}
                </option>
              ))}
            </select>
          )}
        </div>
        
        <div className="top-students-grid">
          {(() => {
            let displayStudents = [];
            
            if (selectedProgram && stats?.topStudentsByProgram) {
              // Show top 3 from selected program
              const programData = stats.topStudentsByProgram.find(p => p._id === selectedProgram);
              if (programData) {
                displayStudents = programData.students;
              }
            } else if (stats?.topStudents) {
              // Show overall top 3
              displayStudents = stats.topStudents;
            }

            return displayStudents && displayStudents.length > 0 ? (
              displayStudents.map((student, index) => (
                <div key={student._id} className="top-student-card">
                  <div className="rank-badge">#{index + 1}</div>
                  <div className="student-photo">
                    {student.photo && student.photo !== 'default-student.png' ? (
                      <img 
                        src={`/uploads/students/${student.photo}`} 
                        alt={student.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <img 
                      src="/default-avatar.png" 
                      alt={student.name}
                      style={{ display: (student.photo && student.photo !== 'default-student.png') ? 'none' : 'block' }}
                    />
                  </div>
                  <div className="student-info">
                    <h4>{student.name}</h4>
                    <p className="student-nim">{student.nim}</p>
                    <p className="student-program">{student.nama_program_studi || student.program}</p>
                    <div className="student-details">
                      <span className="detail-item">
                        <strong>Tahun Masuk:</strong> {student.enrollmentYear || '-'}
                      </span>
                      <span className="detail-item ipk-badge">
                        <strong>IPK:</strong> <span className="ipk-value">{student.ipk?.toFixed(2) || '0.00'}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-data">Belum ada data mahasiswa</p>
            );
          })()}
        </div>
      </div>

      {/* Periode Perbaikan Data Pelaporan */}
      <div className="card">
        <div className="card-header-with-filter">
          <h3><FiCalendar style={{ marginRight: '8px', verticalAlign: 'middle' }} />Periode Perbaikan Data Pelaporan</h3>
          <button 
            className="btn btn-sm btn-outline" 
            onClick={fetchPeriodePerbaikan}
            disabled={loadingPeriode}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <FiRefreshCw className={loadingPeriode ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
        
        {loadingPeriode ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner-small"></div>
          </div>
        ) : periodePerbaikan.length > 0 ? (
          <div className="modern-table-container">
            <table className="modern-table accent-green">
              <thead>
                <tr>
                  <th>Semester</th>
                  <th>Tahun Ajaran</th>
                  <th>Periode Perkuliahan</th>
                  <th>Periode Perbaikan Nilai</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {periodePerbaikan.map((periode) => (
                  <tr key={periode.id_semester}>
                    <td>
                      <strong>{periode.nama_semester}</strong>
                    </td>
                    <td>{periode.tahun_ajaran}</td>
                    <td>
                      {formatDate(periode.tanggal_mulai_perkuliahan)} - {formatDate(periode.tanggal_selesai_perkuliahan)}
                    </td>
                    <td>
                      {periode.tanggal_mulai_perbaikan && periode.tanggal_selesai_perbaikan ? (
                        <span>
                          {formatDate(periode.tanggal_mulai_perbaikan)} - {formatDate(periode.tanggal_selesai_perbaikan)}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Belum ditentukan</span>
                      )}
                    </td>
                    <td>
                      {periode.a_periode_aktif === '1' ? (
                        <span className="table-badge table-badge-success">Aktif</span>
                      ) : (
                        <span className="table-badge table-badge-secondary">Tidak Aktif</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p>Belum ada data periode perbaikan. Pastikan koneksi NEO Feeder sudah dikonfigurasi.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3>Quick Actions</h3>
        <div className="quick-actions">
          <Link to="/students/new" className="btn btn-primary">
            Tambah Mahasiswa Baru
          </Link>
          <Link to="/students" className="btn btn-outline">
            Lihat Semua Mahasiswa
          </Link>
          <Link to="/courses" className="btn btn-outline">
            Kelola Mata Kuliah
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
